import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS } from "@contentful/rich-text-types";
import type { Partner } from "@expert/shared-types";
import { getFeature } from "@soluto-private/expert-workspace-feature-flagging";
import type { ContentfulClientApi, Entry, EntrySkeletonType } from "contentful";
import type {
    ChecklistSection,
    DisplayCue,
    DisplayCueTypes,
    ProductInfo,
    SalesChecklistItem,
    SalesPartnerConfigFields,
} from "../types";
import classes from "./getProductInfo.module.css";

export const getProductInfo = async (
    partner: Partner | "verizonCare",
    employeeId: string | null,
    contentfulClient: ContentfulClientApi<undefined>,
    language: string,
): Promise<{
    products: Record<string, ProductInfo>;
    displayCues: DisplayCue[];
}> => {
    const productInfo = await contentfulClient.withoutUnresolvableLinks.getEntries<
        EntrySkeletonType<SalesPartnerConfigFields, "salesPartnerConfig">
    >({
        content_type: "salesPartnerConfig",
        locale: language,
        "fields.partner": partner,
        include: 10,
    });

    if (!productInfo.items.length) {
        return {
            products: {},
            displayCues: [],
        };
    }
    const context = { partner, employeeId };

    const fields = productInfo.items[0].fields;

    const displayCueCategories = fields.displayCueCategories?.fields.categories;
    const displayCues: DisplayCue[] =
        displayCueCategories?.flatMap((category) => {
            if (!category) return [];
            return category.fields.displayCueItems
                .filter((item) => item?.fields.name)
                .map((item) => ({
                    name: item!.fields.name,
                    rank: item!.fields.rank || 0,
                    detected: false,
                    isDetectedOnPreviousCall: false,
                    type: item!.fields.cueType as DisplayCueTypes,
                }));
        }) || [];

    const filteredList = fields.products.map((item) => item?.fields);
    const productInfoMap: Record<string, ProductInfo> = {};

    for (const item of filteredList) {
        if (item == null) {
            continue;
        }

        productInfoMap[item.productSku] = {
            adGroup: item.adGroup,
            displayName: item.displayName,
            legalDisclosure: documentToReactComponents(item.legalDisclosure, {
                renderNode: {
                    [BLOCKS.PARAGRAPH]: (_node, children) => <p className={classes.paragraph}>{children}</p>,
                    [BLOCKS.LIST_ITEM]: (_node, children) => <li className={classes.listItem}>{children}</li>,
                    [BLOCKS.UL_LIST]: (_node, children) => <ul className={classes.unorderedList}>{children}</ul>,
                },
            }),
            logo: item.logo?.fields.file?.url,
            tagline: item.tagline,
            partnerLogo: item.partnerLogo?.fields.file?.url,
            planBenefits: item.planBenefits,
            confirmationCodeTitle: item.confirmationCodeTitle,
            confirmationCodePlaceholder: item.confirmationCodePlaceholder,
            confirmationCheckboxText: item.confirmationCheckboxText,
            subtext: item.subtext,
            isPrimaryProduct: item.isPrimaryProduct,
            saleSuccessMessage: item.saleSuccessMessage,
            salesOfferChecklist: item.salesOfferChecklist
                ? {
                      checklistSections: await buildChecklistSections(
                          item.salesOfferChecklist.fields.checklistSections,
                          context,
                      ),
                  }
                : undefined,
            sendLinkCommType: item.sendLinkCommType,
            additionalHelpSteps: item.additionalHelpSteps
                ? {
                      checklistSections: await buildChecklistSections(
                          item.additionalHelpSteps.fields.checklistSections,
                          context,
                      ),
                  }
                : undefined,
            productInfoLink: item.productInfoLink,
        };
        if (item.ftcDisclosures?.[0] != null) {
            productInfoMap[item.productSku].ftcDisclosures = {
                checkbox1text: documentToReactComponents(item.ftcDisclosures[0].fields.checkbox1text, {
                    renderNode: {
                        [BLOCKS.PARAGRAPH]: (_node, children) => <p className={classes.paragraph}>{children}</p>,
                        [BLOCKS.LIST_ITEM]: (_node, children) => <li className={classes.listItem}>{children}</li>,
                        [BLOCKS.UL_LIST]: (_node, children) => <ul className={classes.unorderedList}>{children}</ul>,
                    },
                }),
                checkbox2text: documentToReactComponents(item.ftcDisclosures[0].fields.checkbox2text, {
                    renderNode: {
                        [BLOCKS.PARAGRAPH]: (_node, children) => <p className={classes.paragraph}>{children}</p>,
                        [BLOCKS.LIST_ITEM]: (_node, children) => <li className={classes.listItem}>{children}</li>,
                        [BLOCKS.UL_LIST]: (_node, children) => <ul className={classes.unorderedList}>{children}</ul>,
                    },
                }),
                mainText: documentToReactComponents(item.ftcDisclosures[0].fields.mainText, {
                    renderNode: {
                        [BLOCKS.PARAGRAPH]: (_node, children) => <p className={classes.paragraph}>{children}</p>,
                        [BLOCKS.LIST_ITEM]: (_node, children) => <li className={classes.listItem}>{children}</li>,
                        [BLOCKS.UL_LIST]: (_node, children) => <ul className={classes.unorderedList}>{children}</ul>,
                    },
                }),
            };
            if (item.ftcDisclosures[0].fields.nyAddendum != null) {
                productInfoMap[item.productSku].ftcDisclosures!.nyAddendum = documentToReactComponents(
                    item.ftcDisclosures[0].fields.nyAddendum,
                    {
                        renderNode: {
                            [BLOCKS.PARAGRAPH]: (_node, children) => <p className={classes.paragraph}>{children}</p>,
                            [BLOCKS.LIST_ITEM]: (_node, children) => <li className={classes.listItem}>{children}</li>,
                            [BLOCKS.UL_LIST]: (_node, children) => (
                                <ul className={classes.unorderedList}>{children}</ul>
                            ),
                        },
                    },
                );
            }
        }
    }

    return {
        products: productInfoMap,
        displayCues,
    };
};

async function buildChecklistSections(
    checklistSections: (
        | Entry<EntrySkeletonType<ChecklistSection, "checklistSection">, "WITHOUT_UNRESOLVABLE_LINKS", string>
        | undefined
    )[],
    context: Record<string, unknown>,
): Promise<{ title: string; id: string; checklistItems: SalesChecklistItem[] }[]> {
    const definedSections = checklistSections.filter((section) => !!section);
    const featureFlags = Object.fromEntries(
        await Promise.all(
            definedSections
                .flatMap((section) => section.fields.checklistItems)
                .map((item) => item?.fields.featureFlagKey)
                .filter((featureFlagKey) => featureFlagKey !== undefined)
                .map(async (key) => [key, await getFeature<boolean>(key, context)] as const),
        ),
    );
    return definedSections
        .map((section) => ({
            title: section.fields.title,
            id: section.sys.id,
            checklistItems: section.fields.checklistItems
                .filter((item) => !!item)
                .filter((item) => !item.fields.featureFlagKey || featureFlags[item.fields.featureFlagKey])
                .map<SalesChecklistItem>((item) => ({
                    phrase: item.fields.phrase,
                    subheading: item.fields.subheading,
                    highlightColor: item.fields.highlightColor,
                    popupLinkText: item.fields.popupLinkText,
                    popupDescriptionText: item.fields.popupDescriptionText,
                    key: item.fields.key,
                    defaultExample: {
                        description: item.fields.defaultExample?.fields.description,
                        backgroundColor: item.fields.defaultExample?.fields.backgroundColor,
                        icon: item.fields.defaultExample?.fields.icon?.fields.file?.url,
                    },
                    featureFlagKey: item.fields.featureFlagKey,
                    examples: item.fields.examples ?? [],
                    icon: item.fields.icon?.fields.file?.url,
                })),
        }))
        .filter((section) => section.checklistItems?.length > 0);
}
